import { Fragment, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import Input from '@mui/material/Input'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import { signIn, confirmSignIn } from 'aws-amplify/auth'
import { record } from 'aws-amplify/analytics'
import LoaderButton from '../../components/LoaderButton'
import './login.css'
import AccountCircle from '@mui/icons-material/AccountCircle'
import VpnKey from '@mui/icons-material/VpnKey'
import { useNavigate } from 'react-router-dom'
import wedgeLogoDark from '../../assets/img/wedge_logo_dark.svg'
import { showError } from '../../utils/notifications'

const PREFIX = 'login'

const classes = {
  container: `${PREFIX}-container`,
  formControl: `${PREFIX}-formControl`,
  root: `${PREFIX}-root`,
  margin: `${PREFIX}-margin`,
  withoutLabel: `${PREFIX}-withoutLabel`,
  textField: `${PREFIX}-textField`,
}

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.formControl}`]: {
    marginBottom: theme.spacing(1.5),
    '& .MuiInputLabel-root': {
      color: '#909090',
      fontSize: '13px',
      fontWeight: 500,
      transform: 'translate(48px, 20px) scale(1)',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(48px, -8px) scale(0.85)',
        color: '#b0b0b0',
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#e5e5e5',
    },
    '& .MuiInput-root': {
      marginTop: '8px',
      color: '#e5e7eb',
      fontSize: '15px',
      backgroundColor: 'rgba(38, 38, 38, 0.6)',
      borderRadius: '12px',
      padding: '14px 16px 14px 48px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid rgba(115, 115, 115, 0.2)',
      '&:before': {
        display: 'none',
      },
      '&:after': {
        display: 'none',
      },
      '&:hover': {
        backgroundColor: 'rgba(45, 45, 45, 0.7)',
        border: '1px solid rgba(163, 163, 163, 0.3)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(38, 38, 38, 0.8)',
        border: '1px solid rgba(229, 229, 229, 0.4)',
        boxShadow: '0 0 0 3px rgba(229, 229, 229, 0.08)',
      },
      '& input': {
        padding: 0,
        '&:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px rgba(38, 38, 38, 0.9) inset !important',
          WebkitTextFillColor: '#e5e7eb !important',
          caretColor: '#e5e7eb',
          borderRadius: '12px',
          transition: 'background-color 5000s ease-in-out 0s',
        },
        '&:-webkit-autofill:hover': {
          WebkitBoxShadow: '0 0 0 100px rgba(45, 45, 45, 0.9) inset !important',
          WebkitTextFillColor: '#e5e7eb !important',
        },
        '&:-webkit-autofill:focus': {
          WebkitBoxShadow: '0 0 0 100px rgba(38, 38, 38, 0.9) inset !important',
          WebkitTextFillColor: '#e5e7eb !important',
        },
        '&:-webkit-autofill:active': {
          WebkitBoxShadow: '0 0 0 100px rgba(38, 38, 38, 0.9) inset !important',
          WebkitTextFillColor: '#e5e7eb !important',
        },
      },
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      left: '16px',
      marginRight: '0',
      pointerEvents: 'none',
      '& svg': {
        fontSize: '20px',
        color: '#737373',
        opacity: 0.6,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      },
    },
    '& .MuiInput-root:hover .MuiInputAdornment-root svg': {
      opacity: 0.8,
      color: '#909090',
    },
    '& .MuiInput-root.Mui-focused .MuiInputAdornment-root svg': {
      opacity: 1,
      color: '#b0b0b0',
    },
  },

  [`& .${classes.root}`]: {
    display: 'flex',
    flexWrap: 'wrap',
  },

  [`& .${classes.margin}`]: {
    margin: theme.spacing(1),
  },

  [`& .${classes.withoutLabel}`]: {
    marginTop: theme.spacing(3),
  },

  [`& .${classes.textField}`]: {
    flexBasis: 200,
  },
}))

function querystring(name: string, url = window.location.href) {
  name = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i');
  const results = regex.exec(url);

  if (!results) {
    return null;
  }
  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

//Login From UI Testing
const Login = (props: any) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const [state, setState] = useState({
    isLoading: false,
    isNewPassword: false,
    email: '',
    password: '',
    newPassword: '',
    userObject: {},
    name: '',
  })

  const validateForm = () => {
    return state.isNewPassword
      ? state.newPassword.length > 0 && state.email.length > 0 && state.password.length > 0
      : state.email.length > 0 && state.password.length > 0
  }

  const handleChange = (event: any) => {
    setState({
      ...state,
      [event.target.id]: event.target.value,
    })
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    console.log('Submit')
    setState({ ...state, isLoading: true })
    if (state.isNewPassword) {
      try {
        await confirmSignIn({
          challengeResponse: state.newPassword,
          options: {
            userAttributes: {
              name: state.name,
            },
          },
        })
        props.userHasAuthenticated(true, ['*'], ['*'], '/')
        const redirect = querystring('redirect')
        navigate(redirect === '' || redirect === null ? '/' : redirect)
      } catch (error) {
        console.error('Complete new password error:', error)
        showError('Failed to set new password: ' + (error instanceof Error ? error.message : 'Unknown error'))
        setState({ ...state, isLoading: false })

        try {
          await record({ name: '_userauth.auth_fail' })
        } catch (analyticsError) {
          console.warn('Analytics recording failed (non-critical):', analyticsError)
        }
      }
    } else {
      try {
        let result = await signIn({
          username: state.email,
          password: state.password,
        })
        console.log('Sign in result:', result)

        if (result.nextStep && result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setState({ ...state, isNewPassword: true, isLoading: false, userObject: result })
        } else {
          console.log('Sign in successful, authenticating user...')
          props.userHasAuthenticated(true, ['*'], ['*'], '/')

          try {
            await record({ name: '_userauth.sign_in' })
          } catch (analyticsError) {
            console.warn('Analytics recording failed (non-critical):', analyticsError)
          }
          // Note: startSession is no longer available in Amplify v6

          const redirect = querystring('redirect')
          console.log('Navigating to:', redirect === '' || redirect === null ? '/' : redirect)
          navigate(redirect === '' || redirect === null ? '/' : redirect)
        }
      } catch (error) {
        console.error('Sign in error:', error)
        showError('Login failed: ' + (error instanceof Error ? error.message : 'Unknown error'))
        setState({ ...state, isLoading: false })

        try {
          await record({ name: '_userauth.auth_fail' })
        } catch (analyticsError) {
          console.warn('Analytics recording failed (non-critical):', analyticsError)
        }
      }
    }
  }

  return (
    <Root className="Login">
      <form onSubmit={handleSubmit}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img
            src={wedgeLogoDark}
            alt="WEDGE logo"
            style={{ width: '120px', height: 'auto' }}
          />
        </div>
        <h1 className="login-title">{state.isNewPassword ? 'Set New Password' : 'Welcome Back'}</h1>
        {!state.isNewPassword && <p className="login-subtitle">Sign in to your account to continue</p>}
        <div className={classes.container}>
          <FormControl variant="standard" fullWidth className={classes.formControl}>
            <Input
              id="email"
              value={state.email}
              onChange={handleChange}
              autoComplete="email"
              placeholder="Email Address"
              startAdornment={
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              }
            />
          </FormControl>
          <FormControl variant="standard" fullWidth className={classes.formControl}>
            <Input
              id="password"
              value={state.password}
              onChange={handleChange}
              type="password"
              placeholder={state.isNewPassword ? 'Current Password' : 'Password'}
              autoComplete={state.isNewPassword ? 'current-password' : 'password'}
              startAdornment={
                <InputAdornment position="start">
                  <VpnKey />
                </InputAdornment>
              }
            />
          </FormControl>

          {state.isNewPassword ? (
            <Fragment>
              <FormControl variant="standard" fullWidth className={classes.formControl}>
                <Input
                  id="newPassword"
                  value={state.newPassword}
                  onChange={handleChange}
                  type="password"
                  placeholder="New Password"
                  autoComplete="new-password"
                  startAdornment={
                    <InputAdornment position="start">
                      <VpnKey />
                    </InputAdornment>
                  }
                />
              </FormControl>

              <FormControl variant="standard" fullWidth className={classes.formControl}>
                <Input
                  id="name"
                  value={state.name}
                  onChange={handleChange}
                  placeholder="Full Name"
                  autoComplete="name"
                  startAdornment={
                    <InputAdornment position="start">
                      <AccountCircle />
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Fragment>
          ) : (
            ''
          )}

          <FormControl variant="standard" fullWidth className={classes.formControl} style={{ marginTop: '20px', marginBottom: '0', display: 'flex', alignItems: 'center' }}>
            <LoaderButton
              disabled={!validateForm()}
              type="submit"
              text={state.isNewPassword ? 'SET NEW PASSWORD' : 'LOGIN'}
              loadingText={state.isNewPassword ? 'Updating...' : 'Logging in…'}
              isLoading={state.isLoading}
            />
          </FormControl>
        </div>
      </form>
    </Root>
  )
}

export default Login
